import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Plus, Search, Upload, Download } from 'lucide-react';
import { ProspectList } from '@/types/prospect';
import { ProspectCard } from '@/components/ProspectCard';
import { ProspectForm } from '@/components/ProspectForm';
import { CSVUploader } from '@/components/CSVUploader';
import { useOptimizedCampaignData } from '@/contexts/OptimizedCampaignDataContext';
import { TCPAComplianceDialog } from './TCPAComplianceDialog';

interface ProspectListViewProps {
  prospectList: ProspectList;
  onBack: () => void;
  readOnly?: boolean;
  complianceLabelMode?: 'friendly' | 'default';
}

export function ProspectListView({ prospectList, onBack, readOnly = false, complianceLabelMode = 'default' }: ProspectListViewProps) {
  const { updateProspectList, removeProspectFromList, acknowledgeTCPA } = useOptimizedCampaignData();
  const [searchTerm, setSearchTerm] = useState('');
  const [showProspectForm, setShowProspectForm] = useState(false);
  const [showCSVUploader, setShowCSVUploader] = useState(false);
  const [showTCPADialog, setShowTCPADialog] = useState(false);

  const filteredProspects = prospectList.prospects.filter(prospect => 
    prospect.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    prospect.phone.includes(searchTerm) ||
    prospect.company.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleTCPAConfirm = () => {
    acknowledgeTCPA(prospectList.id);
  };

  const handleDelete = (prospectId: string) => {
    const prospect = prospectList.prospects.find(p => p.id === prospectId);
    if (prospect && confirm(`Are you sure you want to remove ${prospect.name} from this list?`)) {
      removeProspectFromList(prospectList.id, prospectId);
    }
  };

  if (!(prospectList.tcpaCompliance || (prospectList as any).tcpa_compliance)) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Lists
          </Button>
        </div>

        <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="text-orange-800">TCPA Compliance Required</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-orange-700 mb-4">
              Before managing prospects in "{prospectList.name}", please acknowledge that you're responsible for list compliance.
            </p>
            <Button 
              onClick={() => setShowTCPADialog(true)}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Acknowledge & Continue
            </Button>
          </CardContent>
        </Card>

        <TCPAComplianceDialog
          open={showTCPADialog}
          onOpenChange={setShowTCPADialog}
          onConfirm={handleTCPAConfirm}
          listName={prospectList.name}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Lists
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{prospectList.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="default" className="bg-green-600">
                {complianceLabelMode === 'friendly' ? 'Compliance Acknowledged' : 'TCPA Compliant'}
              </Badge>
              {readOnly && (
                <Badge variant="outline" className="text-xs">
                  Demo view — read-only
                </Badge>
              )}
              <span className="text-muted-foreground">
                {prospectList.prospects.length} prospects
              </span>
            </div>
          </div>
        </div>
        {!readOnly && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowCSVUploader(true)}>
              <Upload className="h-4 w-4 mr-2" />
              Upload CSV
            </Button>
            <Button onClick={() => setShowProspectForm(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Prospect
            </Button>
          </div>
        )}
      </div>

      {/* Description */}
      {prospectList.description && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground">{prospectList.description}</p>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Search Prospects</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, phone, or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Prospects Grid */}
      {filteredProspects.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProspects.map((prospect) => (
            <ProspectCard
              key={prospect.id}
              prospect={prospect}
              onDelete={handleDelete}
              listId={prospectList.id}
              readOnly={readOnly}
            />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto">
                <Plus className="h-8 w-8 text-muted-foreground" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">
                  {prospectList.prospects.length === 0 ? 'No prospects yet' : 'No prospects match your search'}
                </h3>
                <p className="text-muted-foreground">
                  {prospectList.prospects.length === 0 
                    ? 'Add your first prospect to get started' 
                    : 'Try adjusting your search term'
                  }
                </p>
              </div>
              {prospectList.prospects.length === 0 && !readOnly && (
                <div className="flex gap-2">
                  <Button onClick={() => setShowProspectForm(true)} className="gap-2">
                    <Plus className="h-4 w-4" />
                    Add Manually
                  </Button>
                  <Button variant="outline" onClick={() => setShowCSVUploader(true)} className="gap-2">
                    <Upload className="h-4 w-4" />
                    Upload CSV
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Prospect Form Dialog */}
      <ProspectForm
        open={showProspectForm}
        onOpenChange={setShowProspectForm}
        listId={prospectList.id}
      />

      {/* CSV Uploader Dialog */}
      <CSVUploader
        open={showCSVUploader}
        onOpenChange={setShowCSVUploader}
        listId={prospectList.id}
      />
    </div>
  );
}